import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from "./Demo.module.css"
import { useNavigate, useSearchParams } from 'react-router-dom'
import LogoItem from '../Logo/LogoItem'
import ErrorPage from '../Error/js/ErrorPage'
import { BsCheck } from 'react-icons/bs'
import { useDispatch } from 'react-redux';
import { SET_AUTH } from '../../actions/authTypes'
import { axiosInstance } from '../../axios/publicAxios'

const FAKE_STEP_CAP = 3
const TOTAL_STEPS = 4
    
const Demo = ({status}) => {
    const [currentStep, setCurrentStep] = useState(0)
    const [crashError, setCrashError] = useState(false)
    const [forbiddenError, setForbiddenError] = useState(false)
    const [incompleteError, setIncompleteError] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const dispatch = useDispatch()
    const token = searchParams.get("session")
    const fakeIntervalRef = useRef(null)
    const startedDemo = useRef(false)

    const startFakeProgress = () => {
        let step = 0
        fakeIntervalRef.current = setInterval(() => {
            step += 1
            setCurrentStep(Math.min(step, FAKE_STEP_CAP))
        }, 1200)
    }

    const stopFakeProgress = () => {
        if (fakeIntervalRef.current) clearInterval(fakeIntervalRef.current)
        fakeIntervalRef.current = null
    }


    const startDemo = useCallback(async () => {
        startFakeProgress()
        try {
            const response = await axiosInstance.post("/demo-entry/workspace", { token }, { withCredentials: true })
            stopFakeProgress()
            setCurrentStep(TOTAL_STEPS)
            setTimeout(()=>{
                dispatch({ type: SET_AUTH, payload: response.data })
                localStorage.setItem("login-event", new Date())
                navigate("/notes")
            },3000)
        } catch (err) {
            if (err.response?.status === 403) {
                setForbiddenError(true)
                return
            }
            setCrashError(true)
            return
        }
    }, [dispatch, navigate, token])
    
    useEffect(() => {
        if (status !== 'inactive') return

        if (!token) {
            setIncompleteError(true)
            return
        }

        if (!startedDemo.current){
            startedDemo.current = true
            fakeIntervalRef.current = null
            startDemo()
        }

        return () => {
            stopFakeProgress()
        }
    }, [token, status,startDemo])

    return (
        crashError
            ? <CrashPage handleRetry={() => {
                setCrashError(false)
                startDemo()
            }} /> :
        forbiddenError ? <ForbiddenPage/> :
        incompleteError ? <MissingTokenPage/> :
        <div className = {styles.wrapper}>
            <header>
                <div className = {styles.headerContent}>
                    <LogoItem/>
                </div>
            </header>
            <DemoLoadingScreen currentStep = {currentStep}/>
            <footer>
                <p>© {new Date().getFullYear()} NotePro. All rights reserved.</p>
            </footer>
        </div>
    )
}

const STEPS = [
  'Confirming your access',
  'Setting up your workspace',
  'Adding sample notes and tags',
  'Signing you in',
]

const StepIcon = ({ status }) => {
    if (status === 'done') {
        return (
            <div className={styles.iconDone}>
                <BsCheck/>
            </div>
        )
    }
    if (status === 'active') {
        return <div className={styles.iconSpinner} />
    }
    return <div className={styles.iconPending} />
}

const DemoLoadingScreen = ({ currentStep = 0 }) => {
    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <div className={styles.badge}>
                    <div className={styles.badgeSpinner} />
                    <span>Setting up</span>
                </div>
            <h1 className={styles.title}>Setting up your demo account…</h1>
                <p className={styles.subtitle}>
                    We're preparing your temporary environment. This usually takes a few seconds.
                </p>
                <div className={styles.steps}>
                    {STEPS.map((label, i) => {
                        const status = i < currentStep ? 'done' : i === currentStep ? 'active' : 'pending'
                        return (
                            <div key={label} className={styles.step}>
                                <StepIcon status={status} />
                                <span className={status === 'pending' ? styles.stepLabelPending : styles.stepLabel}>
                                {label}
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>
            <div className={styles.illustration}>
                <div className={`${styles.card} ${styles.cardBlue}`}>
                    <div className={styles.line} style={{ width: '85%' }} />
                    <div className={styles.line} style={{ width: '70%' }} />
                    <div className={styles.line} style={{ width: '60%', marginBottom: 14 }} />
                    <span className={`${styles.tag} ${styles.tagBlue}`}>Notes</span>
                </div>
                <div className={`${styles.card} ${styles.cardPurple}`}>
                    <div className={styles.line} style={{ width: '80%' }} />
                    <div className={styles.line} style={{ width: '65%', marginBottom: 14 }} />
                    <span className={`${styles.tag} ${styles.tagPurple}`}>Tags</span>
                </div>
                <div className={`${styles.card} ${styles.cardGreen}`}>
                    <div className={styles.line} style={{ width: '75%' }} />
                    <div className={styles.line} style={{ width: '55%', marginBottom: 14 }} />
                    <span className={`${styles.tag} ${styles.tagGreen}`}>Demo</span>
                </div>
            </div>
        </div>
  )
}

const ForbiddenPage = () => {
    return (
        <ErrorPage
            errorStatus = {403}
            errorMessage = "Demo Session Expired"
            errorDesc = "Your demo access token is no longer valid. Please reopen the demo from your résumé link."
            page = "demo"
        />
    )
}

export const CrashPage = ({handleRetry}) => {
    return(
        <ErrorPage
            errorStatus={500}
            errorMessage="Couldn't Start Your Demo"
            errorDesc="Something went wrong while setting things up. Please try reopening the demo link — if the problem continues, it may be a temporary issue on our end."
            handleRetry={()=>handleRetry()}
            page="demo"
        />
    )
}

const MissingTokenPage = () => {
    return (
        <ErrorPage
            errorStatus={403}
            errorMessage="Demo Link Incomplete"
            errorDesc="This link is missing the information needed to start your demo. Please use the demo link from my résumé or portfolio."
            page="demo"
        />
    )
}

export default Demo